import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import './Header.css';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { disablePushNotifications } from '../utils/notificationService';
import { trackEvent } from '../analytics';
import CreditsDisplay from './CreditsDisplay';
import { MOTIONPACE_MARKETING_ENABLED } from '../lib/motionPaceMarketing';
import { OPERATIONS, MOTION_MODES } from '../lib/operationCopy';
import { displayNameOf } from '../lib/operations';

// The commercial and reference destinations, flat. They used to be a
// "Resources" dropdown (Guides + Calculator) beside a lone Pricing link, which
// hid two of them behind a click and gave the header two different interaction
// models. `analytics` is the nav_click target, kept as it was so the existing
// funnel is uninterrupted.
const NAV_LINKS = [
  { to: '/studio', label: 'Studio', analytics: 'header_studio' },
  { to: '/pricing', label: 'Pricing', analytics: 'header_pricing' },
  { to: '/guides', label: 'Guides', analytics: 'header_guides' },
  { to: '/tools/timelapse-calculator', label: 'Calculator', analytics: 'header_calculator' },
];

// The Features tab, and everything reachable from it.
//
// #544 shipped a flat "Features" link pointing straight at /motionpace
// (nav_click target `header_motionpace`). #564 replaces it with this dropdown,
// because there are now three feature pages and sending a tab labelled
// "Features" to just one of them was already wrong. The dropdown names all
// three in the header itself, and the new /features index sits under it as
// "All features".
//
// ANALYTICS: this deliberately breaks the funnel. `header_motionpace` — the
// target live in main today — stops being emitted, and `header_features` plus
// the three `header_features_*` children start from zero. A flat
// `header_motionpace` line in GA4 after this ships is expected, not a
// regression. The old target is not reused because the destination changed:
// counting index-page clicks as MotionPace clicks would understate a metric
// that used to mean "went to the MotionPace page".
//
// Still a dropdown rather than three more flat tabs: three feature names beside
// Studio/Pricing/Guides/Calculator is seven top-level tabs, which is where a
// header stops being scannable.
//
// The WHOLE structure — labels included — sits behind the gate and evaluates to
// null, rather than being assembled and then hidden at render time. That is
// what keeps the feature out of a dark build: everything that names it is data
// on this one object, so the minifier folds it away with the flag. Verified
// against a real `build:production` — the dark bundle contains no feature
// route, no display name, and none of the header_features_* targets.
//
// What DOES survive that build is the FeaturesDropdown markup below — class
// names, testid, caret path — as unreachable code terser does not prove dead.
// It renders nothing, because everything it renders comes from this object. If
// that ever has to go too, the fix is App.js's pattern: move the component to
// its own file and `require()` it behind the raw `process.env` check, which is
// the form CRA's DefinePlugin can fold at parse time.
//
// Names come from the naming source (#379: operationCopy.js / operations.js),
// never retyped here — the same rule FeaturesPage and MotionTrimPage follow.
const FEATURES_MENU = MOTIONPACE_MARKETING_ENABLED
  ? {
      label: 'Features',
      items: [
        {
          to: '/motionpace',
          label: OPERATIONS.variable_speed.displayName,
          analytics: 'header_features_motionpace',
          testId: 'features-motionpace',
        },
        {
          to: '/motiontrim',
          label: MOTION_MODES.trim.name,
          analytics: 'header_features_motiontrim',
          testId: 'features-motiontrim',
        },
        {
          to: '/deflicker-timelapse',
          label: displayNameOf('deflicker'),
          analytics: 'header_features_deflicker',
          testId: 'features-deflicker',
        },
      ],
      // Both the /features route and this `header_features` target are new in
      // #564 — there is no prior series to continue. See the analytics note
      // above.
      index: {
        to: '/features',
        label: 'All features',
        analytics: 'header_features',
        testId: 'features-all',
      },
    }
  : null;

// Routes that belong to the Features tab. The trigger is not a NavLink, so it
// has no `isActive` of its own — it takes the underline when the current route
// is any of its children.
const FEATURE_ROUTES = FEATURES_MENU
  ? [FEATURES_MENU.index.to, ...FEATURES_MENU.items.map((item) => item.to)]
  : [];

const isFeatureRoute = (pathname) =>
  FEATURE_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));

/**
 * The desktop Features tab: a click-to-open disclosure listing the three
 * feature pages plus the index.
 *
 * Click, not hover. Hover menus have no open state to announce to assistive
 * tech and are unreachable on the touch laptops and tablets a good share of
 * this traffic arrives on.
 *
 * Its own state and ref rather than the overflow menu's: the two panels are
 * never open at the same viewport (this one is desktop-only, the overflow menu
 * is <=768px), so sharing state would only couple them.
 */
const FeaturesDropdown = ({ menu, active, onSelect }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    // Same mousedown-outside pattern the overflow menu uses, so both panels
    // dismiss on the same gesture.
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        // Focus goes back to the trigger, not to the top of the document — a
        // keyboard user who backs out of the menu keeps their place in the nav.
        if (triggerRef.current) triggerRef.current.focus();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleSelect = (analytics) => () => {
    onSelect(analytics);
    setOpen(false);
  };

  const itemClass = (extra) => ({ isActive }) =>
    `overflow-menu-item${extra ? ` ${extra}` : ''}${isActive ? ' is-active' : ''}`;

  return (
    <div className="header-features" ref={wrapRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`header-nav-link header-features-trigger${active ? ' is-active' : ''}`}
        data-testid="header-features-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        {menu.label}
        <svg
          className={`header-features-caret${open ? ' is-open' : ''}`}
          width="10"
          height="6"
          viewBox="0 0 10 6"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M1 1l4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className="header-features-menu" data-testid="header-features-menu">
          {menu.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={itemClass()}
              data-testid={`header-${item.testId}-link`}
              onClick={handleSelect(item.analytics)}
            >
              {item.label}
            </NavLink>
          ))}
          <div className="overflow-menu-divider" />
          <NavLink
            to={menu.index.to}
            className={itemClass('header-features-all')}
            data-testid={`header-${menu.index.testId}-link`}
            onClick={handleSelect(menu.index.analytics)}
          >
            {menu.index.label}
            <span className="header-features-all-arrow" aria-hidden="true">
              →
            </span>
          </NavLink>
        </div>
      )}
    </div>
  );
};

const Header = ({ user, signIn, credits, creditsLoading, onToggleDrawer, drawerOpen }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { pathname } = useLocation();

  const signOutFunc = async () => {
    trackEvent('sign_out', {});
    // Drop the FCM token first so this device stops getting notifications
    // for the signed-out user. Best-effort — ignore failures so the user
    // can always sign out.
    try { await disablePushNotifications(); } catch (_) { /* noop */ }
    signOut(auth);
  };

  const handleSignInClick = () => {
    trackEvent('cta_click', { cta_id: 'header_sign_in', signed_in: false });
    signIn();
  };

  const handleAccountClick = () => {
    trackEvent('nav_click', { target: 'account' });
  };

  // Direct form for callers that already have the target in hand (the Features
  // dropdown); curried form for the `onClick={...}` sites below.
  const trackNavClick = (target) => {
    trackEvent('nav_click', { target });
    setMenuOpen(false);
  };

  const handleNavClick = (target) => () => trackNavClick(target);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  return (
    <header className="App-header">
      <div className="header-left">
        <div className="logo-container">
          {user && onToggleDrawer && (
            <button
              className="btn-drawer-toggle"
              onClick={onToggleDrawer}
              aria-label={drawerOpen ? 'Close projects menu' : 'Open projects menu'}
              aria-expanded={drawerOpen}
            >
              {drawerOpen ? '✕' : '☰'}
            </button>
          )}
          <Link to="/">
            <img src="/TweediaStudioDarkMode.svg" alt="Tweedia Studio Logo" className="logo-header" />
          </Link>
        </div>
        <nav className="header-nav header-desktop-only" aria-label="Main">
          {FEATURES_MENU && (
            <FeaturesDropdown
              menu={FEATURES_MENU}
              active={isFeatureRoute(pathname)}
              onSelect={trackNavClick}
            />
          )}
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) => `header-nav-link${isActive ? ' is-active' : ''}`}
              data-testid={`header-${link.analytics.replace('header_', '')}-link`}
              onClick={handleNavClick(link.analytics)}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>
      </div>
      <div className="auth-container">
        {user && <CreditsDisplay credits={credits} loading={creditsLoading} />}
        {user && (
          <Link to="/account" className="btn-account header-desktop-only" data-testid="header-account-link" onClick={handleAccountClick}>
            Account
          </Link>
        )}
        {user ? (
          <button className="btn-signin header-desktop-only" onClick={signOutFunc}>Sign Out</button>
        ) : (
          <button className="btn-signin" onClick={handleSignInClick}>Sign In</button>
        )}
        <div className="header-overflow" ref={menuRef}>
          <button
            className="btn-overflow"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="More options"
            aria-expanded={menuOpen}
          >
            &#8942;
          </button>
          {menuOpen && (
            <div className="overflow-menu">
              {/* No nested dropdown on mobile — a second panel inside a panel
                  is a dead end on a touch screen. The tab's four destinations
                  ride along as an indented, labelled group instead. */}
              {FEATURES_MENU && (
                <>
                  <div className="overflow-menu-group-label">{FEATURES_MENU.label}</div>
                  {[...FEATURES_MENU.items, FEATURES_MENU.index].map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `overflow-menu-item overflow-menu-item-nested${
                          item === FEATURES_MENU.index ? ' header-features-all' : ''
                        }${isActive ? ' is-active' : ''}`
                      }
                      data-testid={`overflow-${item.testId}-link`}
                      onClick={handleNavClick(item.analytics)}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                  <div className="overflow-menu-divider" />
                </>
              )}
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) => `overflow-menu-item${isActive ? ' is-active' : ''}`}
                  onClick={handleNavClick(link.analytics)}
                >
                  {link.label}
                </NavLink>
              ))}
              {user && (
                <>
                  <div className="overflow-menu-divider" />
                  <Link to="/account" className="overflow-menu-item" onClick={() => setMenuOpen(false)}>
                    Account
                  </Link>
                  <button className="overflow-menu-item" onClick={() => { signOutFunc(); setMenuOpen(false); }}>
                    Sign Out
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
